'use client';

import { useEffect } from 'react';
import { GATE_THRESHOLDS, isInViewEnough } from '@/lib/website/motion-gate';

/**
 * Scroll gating for every animation on the marketing page.
 *
 * One IntersectionObserver watches anything marked `data-fv-motion`. Each block
 * plays exactly ONE pass, the first time it comes properly into view, and then
 * holds its end state forever: the element is unobserved the moment it has been
 * released.
 *
 * NOT rewound on exit. Replaying on the way back up means the page re-animates
 * itself every time you scroll past anything, which is restless rather than
 * alive: a counter that climbs to 1,189 every single time you pass it stops
 * reading as data and starts reading as a screensaver.
 *
 * THIS HOLDS ANIMATIONS BACK. IT DOES NOT RESTART THEM, and that distinction is
 * the whole reason the file reads the way it does.
 *
 * Every animated block carries its animation as an inline style, which means it
 * is in the server-rendered HTML and starts on first paint. Measured on the
 * live page: at scrollY 0, with the core panel 5,151px below the viewport, its
 * rows were already mid-animation at opacity 0.33. The whole eight-row assembly
 * finished about 1.3 seconds after load, entirely unseen.
 *
 * The previous version of this file then "gated" that by RESTARTING it on
 * arrival: clear `animation`, force a reflow, put the value back. So a block
 * you had already scrolled to and were looking at would blink out, snap to the
 * head of its keyframes and re-assemble in front of you. That is the glitch,
 * and no amount of tuning the threshold could fix it, because the animation had
 * already run before the observer ever got a say.
 *
 * So nothing is restarted here. The paired CSS in globals.css holds every block
 * at `animation-play-state: paused` from the very first frame, and all this
 * does is stamp `data-fv-played` when the block arrives, which stops the pause
 * rule matching and lets it play once, from the beginning, in view. No reflow
 * hack, no cancel-and-restore, no reading of `getAnimations()`.
 *
 * It must FAIL OPEN. These keyframes start at opacity 0, so a block that is
 * never released is not a missing animation, it is missing content. Every early
 * return below releases everything on its way out.
 */
export function useScrollGatedMotion() {
  useEffect(() => {
    const release = (el: Element) => el.setAttribute('data-fv-played', 'true');
    const releaseEverything = () =>
      document.querySelectorAll('[data-fv-motion]').forEach(release);

    if (typeof IntersectionObserver === 'undefined') {
      releaseEverything();
      return;
    }

    // Reduced motion releases the lot in one go rather than bailing out and
    // leaving everything paused. The paired media query collapses the durations
    // to nothing, so the blocks arrive at their end state instead of forty
    // animations playing at once, which is the opposite of what was asked for.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      releaseEverything();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const enough = isInViewEnough({
            ratio: entry.intersectionRatio,
            visibleHeight: entry.intersectionRect.height,
            rootHeight: entry.rootBounds?.height ?? 0,
          });
          if (!enough) return;
          // ONCE. Stop watching this block the moment it has been let go.
          observer.unobserve(entry.target);
          release(entry.target);
        });
      },
      {
        // Thresholds on the way in, not just one at the top: see the comment on
        // isInViewEnough for why a single 0.45 is unreachable for a tall block.
        threshold: GATE_THRESHOLDS,
        // The inset means the element must be properly up the viewport before
        // anything starts, rather than the instant its first pixel appears.
        rootMargin: '0px 0px -18% 0px',
      },
    );

    // Observe once per element, ever. An earlier version re-created the
    // observer on every render (the effect had no dependency array), which
    // disconnected and re-observed everything each time. The MutationObserver
    // picks up overlay content, which mounts long after the page does.
    const seen = new WeakSet<Element>();
    const observeAll = (root: ParentNode) => {
      root.querySelectorAll('[data-fv-motion]').forEach((el) => {
        if (seen.has(el)) return;
        seen.add(el);
        observer.observe(el);
      });
    };
    observeAll(document);

    const mutations = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('[data-fv-motion]') && !seen.has(node)) {
            seen.add(node);
            observer.observe(node);
          }
          observeAll(node);
        });
      });
    });
    mutations.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, []);
}

/** Shorthand for the animation shorthand strings, which are otherwise noisy. */
export function anim(name: string, delay = 0, duration = '3.4s', easing = 'ease-out'): string {
  return `${name} ${duration} ${easing} ${delay}s both`;
}
